import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { motion } from 'motion/react'
import { AppShell } from '@/shared/ui/app-shell'
import { BottomNav } from '@/widgets/bottom-nav'
import { CatalogHeader } from '@/widgets/catalog-header'
import { CatalogSectionSwitcher } from '@/widgets/catalog-section-switcher'
import { HomeSearch } from '@/widgets/home-search'
import { CatalogCategoriesChips } from '@/widgets/catalog-categories-chips'
import { CatalogTeaGrid } from '@/widgets/catalog-tea-grid'
import { CatalogTeawareGrid } from '@/widgets/catalog-teaware-grid'
import { CatalogGroupedSections } from '@/widgets/catalog-grouped-sections'
import { CatalogGroupedTeawareSections } from '@/widgets/catalog-grouped-teaware-sections'
import { CatalogFilters } from '@/widgets/catalog-filters'
import { CatalogSort } from '@/widgets/catalog-sort'
import { CatalogFloatingFilters } from '@/widgets/catalog-floating-filters'
import { CatalogSetsGrid } from '@/widgets/catalog-sets-grid'
import { useInfiniteTea, useInfiniteTeaware, useProductSets } from '@/entities/product/api/use-products'
import { useTeaCategories, useTeawareCategories } from '@/entities/product/api/use-categories'
import { useTelegram } from '@/shared/lib/telegram'
import { useScrollRestoration } from '@/shared/lib/use-scroll-restoration'
import { useCatalogFilters } from '@/shared/lib/use-catalog-filters'
import type { Tea, Teaware, ProductSet } from '@/entities/product'
import { useEffect, useRef } from 'react'

// Схема для query параметров
const catalogSearchSchema = z.object({
  category: z.string().optional(),
  subcategory: z.string().optional(),
  sort: z.enum(['popular', 'price-asc', 'price-desc', 'newest']).optional(),
  search: z.string().optional(),
})

// Схема для параметров маршрута
const catalogParamsSchema = z.object({
  section: z.enum(['tea', 'teaware', 'product_set']),
})

export const Route = createFileRoute('/catalog/$section')({
  validateSearch: catalogSearchSchema,
  params: {
    parse: (params) => catalogParamsSchema.parse(params),
    stringify: (params) => ({ section: params.section }),
  },
  component: CatalogPage,
})

function CatalogPage() {
  const { section } = Route.useParams()
  const { category, subcategory, sort, search } = Route.useSearch()
  const { hapticFeedback } = useTelegram()

  // Сохранение/восстановление фильтров при навигации
  useCatalogFilters(section, { category, subcategory, sort, search })

  // Scroll to top при смене секции
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [section])
  
  // Получаем категории с сервера
  const teaCategoriesQuery = useTeaCategories()
  const teawareCategoriesQuery = useTeawareCategories()
  
  const categoriesQuery = section === 'tea' ? teaCategoriesQuery : teawareCategoriesQuery
  const rawCategories = categoriesQuery.data?.data || []
  
  // Преобразуем категории в формат для CatalogFilters
  // Используем id (UUID) для фильтрации на бэкенде
  const categories = rawCategories.map((cat) => ({
    value: cat.id,
    label: cat.name,
  }))
  
  // Нормализуем category и subcategory
  const normalizedCategory = category && category.trim() ? category.trim() : undefined
  const normalizedSubcategory = subcategory && subcategory.trim() ? subcategory.trim() : undefined
  
  // Получаем данные товаров с бесконечной прокруткой
  // По умолчанию показываем все товары (category = undefined)
  // Используем большой лимит для получения всех товаров сразу
  const teaInfiniteQuery = useInfiniteTea({
    category: normalizedCategory,
    subcategory: normalizedSubcategory,
    sort: sort || 'popular',
    search,
  }, 1000) // Запрашиваем до 1000 товаров за раз
  
  const teawareInfiniteQuery = useInfiniteTeaware({
    category: normalizedCategory,
    subcategory: normalizedSubcategory,
    sort: sort || 'popular',
    search,
  }, 1000) // Запрашиваем до 1000 товаров за раз

  // Наборы — отдельный хук, грузим только на вкладке product_set
  const productSetsQuery = useProductSets({
    sort: sort || 'popular',
    search,
    purchasableOnly: true,
  })

  const productsQuery = section === 'tea'
    ? teaInfiniteQuery
    : section === 'teaware'
      ? teawareInfiniteQuery
      : productSetsQuery

  // Объединяем все страницы в один массив и фильтруем undefined элементы
  const teas = (teaInfiniteQuery.data?.pages.flatMap(page => page.data) || []).filter((item): item is Tea => item != null)
  const teaware = (teawareInfiniteQuery.data?.pages.flatMap(page => page.data) || []).filter((item): item is Teaware => item != null)
  const productSets: ProductSet[] = productSetsQuery.data?.data || []
  const isLoading = productsQuery.isLoading
  const isError = productsQuery.isError

  // Пагинация есть только у чай/посуда (infinite). Наборы грузятся одним списком.
  const isInfinite = section === 'tea' || section === 'teaware'
  const hasNextPage = isInfinite ? (teaInfiniteQuery.hasNextPage || teawareInfiniteQuery.hasNextPage) : false
  const isFetchingNextPage = isInfinite ? (teaInfiniteQuery.isFetchingNextPage || teawareInfiniteQuery.isFetchingNextPage) : false
  const fetchNextPage = isInfinite
    ? (section === 'tea' ? teaInfiniteQuery.fetchNextPage : teawareInfiniteQuery.fetchNextPage)
    : undefined
  
  // Ref для контейнера скролла (с восстановлением позиции при навигации назад)
  const scrollContainerRef = useScrollRestoration(`catalog-${section}`)
  // Ref для блока с фильтрами
  const filtersRef = useRef<HTMLDivElement>(null)
  
  // Обработчик скролла для автоматической загрузки следующей страницы
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return
    
    const handleScroll = () => {
      // Проверяем, есть ли следующая страница и не загружается ли она уже
      if (!hasNextPage || isFetchingNextPage) return
      
      const { scrollTop, scrollHeight, clientHeight } = container
      // Загружаем следующую страницу, когда пользователь прокрутил до 80% контента
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight
      
      if (scrollPercentage > 0.8) {
        fetchNextPage()
      }
    }
    
    // Используем throttling для оптимизации производительности
    let ticking = false
    const throttledHandleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }
    
    container.addEventListener('scroll', throttledHandleScroll, { passive: true })
    
    return () => {
      container.removeEventListener('scroll', throttledHandleScroll)
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])
  
  // Унифицированный layout для обеих секций
  return (
    <AppShell showBottomNav>
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden bg-paper dark:bg-[#0F1113] pt-safe"
      >
        {/* Контейнер с адаптивными отступами */}
        <div className="content-container py-6">
          {/* Header с логотипом и кнопкой профиля */}
          <CatalogHeader />
          
          {/* Переключатель Чай / Посуда */}
          <div className="mt-6">
            <CatalogSectionSwitcher currentSection={section} />
          </div>
          
          {/* Поиск */}
          <div className="mt-4">
            <HomeSearch section={section} syncWithUrl />
          </div>
          
          {/* Desktop: две колонки - фильтры слева, товары справа */}
          <div className="mt-6 lg:grid lg:grid-cols-12 lg:gap-8">
            {/* Левая колонка - фильтры (только на desktop) */}
            <aside className="hidden lg:block lg:col-span-3 space-y-6">
              {/* Популярные направления */}
              <div className="widget-card">
                <h3 className="section-title text-lg mb-4">
                  Популярные направления
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Выберите стиль, который вам ближе
                </p>
                <CatalogCategoriesChips section={section} />
              </div>
              
              {/* Фильтры */}
              <div ref={filtersRef} className="widget-card">
                <h3 className="section-title text-lg mb-4">Фильтры</h3>
                <div className="space-y-3">
                  <CatalogFilters
                    currentCategory={normalizedCategory}
                    currentSubcategory={normalizedSubcategory}
                    categories={categories}
                    section={section}
                  />
                  <CatalogSort currentSort={sort} />
                </div>
              </div>
            </aside>

            {/* Правая колонка / основной контент */}
            <div className="lg:col-span-9">
              {/* Mobile: Популярные направления (чипы) */}
              <div className="lg:hidden mb-4">
                <div className="mb-3">
                  <h3 className="text-lg font-semibold text-ink dark:text-white mb-1">
                    Популярные направления
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Выберите стиль, который вам ближе
                  </p>
                </div>
                <CatalogCategoriesChips section={section} />
              </div>
              
              {/* Заголовок каталога с фильтрами (mobile) */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-ink dark:text-white">
                      {section === 'tea' ? 'Каталог чая' : section === 'teaware' ? 'Каталог посуды' : 'Подарочные наборы'}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 hidden sm:block">
                      {section === 'tea'
                        ? 'Найдите сорт, который хотите попробовать или сохранить в дневнике'
                        : section === 'teaware'
                          ? 'Чайники, гайвани и аксессуары для идеального заваривания'
                          : 'Готовые комплекты чая и посуды — цена ниже, чем по отдельности'}
                    </p>
                  </div>
                </div>
                
                {/* Mobile: Фильтры и сортировка */}
                <div ref={!filtersRef.current ? filtersRef : undefined} className="flex items-center gap-2 flex-wrap lg:hidden">
                  <CatalogFilters
                    currentCategory={normalizedCategory}
                    currentSubcategory={normalizedSubcategory}
                    categories={categories}
                    section={section}
                  />
                  <CatalogSort currentSort={sort} />
                </div>
              </div>
          
        
        {/* Основной каталог */}
        {isError ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="px-4 py-16 text-center"
          >
            <div className="mb-6">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="mx-auto w-24 h-24 rounded-full bg-primary/10 dark:bg-primary-dark/20 flex items-center justify-center mb-4"
              >
                {/* Фирменная иконка: кружок + чайная чашка */}
                <svg
                  className="w-12 h-12 text-primary dark:text-primary-light"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {/* Кружок */}
                  <circle cx="12" cy="12" r="10" strokeWidth={1.5} />
                  {/* Чайная чашка */}
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8 10h8M8 10v6a2 2 0 002 2h4a2 2 0 002-2v-6M8 10l1-3a1 1 0 011-1h4a1 1 0 011 1l1 3M10 16h4"
                  />
                </svg>
              </motion.div>
            </div>
            <h3 className="text-[22px] font-bold text-ink dark:text-white mb-2">
              Не удалось загрузить каталог
            </h3>
            <p className="text-[15px] text-gray-600 dark:text-gray-400 mb-6 max-w-sm mx-auto leading-relaxed">
              Проверьте подключение к интернету и попробуйте ещё раз.
            </p>
            <motion.button
              whileTap={{ scale: 0.97 }}
              type="button"
              onClick={() => {
                hapticFeedback.impactOccurred('medium')
                productsQuery.refetch()
              }}
              className="px-8 py-3.5 bg-primary dark:bg-primary-dark text-white rounded-[16px] font-semibold touch-target min-h-[56px] shadow-lg hover:shadow-xl transition-all text-base"
            >
              Повторить
            </motion.button>
          </motion.div>
        ) : (
          <>
            {section === 'tea' ? (
              // При поиске показываем плоский список, иначе группировка по подкатегориям
              search ? (
                <CatalogTeaGrid
                  teas={teas}
                  isLoading={isLoading}
                  skeletonCount={5}
                />
              ) : (
                <CatalogGroupedSections
                  teas={teas}
                  isLoading={isLoading}
                  selectedCategoryId={normalizedCategory}
                  selectedSubcategoryId={normalizedSubcategory}
                />
              )
            ) : section === 'teaware' ? (
              // При поиске показываем плоский список, иначе группировка по подкатегориям
              search ? (
                <CatalogTeawareGrid
                  teaware={teaware}
                  isLoading={isLoading}
                  skeletonCount={6}
                />
              ) : (
                <CatalogGroupedTeawareSections
                  teaware={teaware}
                  isLoading={isLoading}
                  selectedCategoryId={normalizedCategory}
                  selectedSubcategoryId={normalizedSubcategory}
                />
              )
            ) : (
              // Наборы — плоский список без категорий/группировки
              <CatalogSetsGrid
                sets={productSets}
                isLoading={isLoading}
                skeletonCount={4}
              />
            )}
            
            {/* Индикатор загрузки следующей страницы */}
            {isFetchingNextPage && (
              <div className="px-4 py-6 flex justify-center">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <div className="w-5 h-5 border-2 border-primary dark:border-primary-light border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm">Загрузка...</span>
                </div>
              </div>
            )}
            
            {/* Показываем посуду после чая, если нет выбранной категории */}
            {section === 'tea' && !normalizedCategory && !search && (
              <>
                {/* Разделитель */}
                <div className="px-4 py-8">
                  <div className="h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-dark-border to-transparent" />
                </div>
                
                {/* Заголовок секции посуды */}
                <div className="px-4 mb-4">
                  <h2 className="text-[20px] font-bold text-ink dark:text-white mb-1">
                    Каталог посуды
                  </h2>
                  <p className="text-[13px] text-gray-600 dark:text-gray-400">
                    Чайники, гайвани и аксессуары для идеального заваривания
                  </p>
                </div>
                
                {/* Группированная посуда по категориям */}
                <CatalogGroupedTeawareSections
                  teaware={teaware}
                  isLoading={teawareInfiniteQuery.isLoading}
                />
              </>
            )}
          </>
        )}
        
            </div>
          </div>
        </div>
        
      </div>
      
      {/* Плавающая кнопка фильтров - только на mobile */}
      <div className="lg:hidden">
        <CatalogFloatingFilters
          currentCategory={normalizedCategory}
          categories={categories}
          filtersRef={filtersRef}
        />
      </div>
      
      {/* Нижняя навигация (скрыта на desktop) */}
      <BottomNav />
    </AppShell>
  )
}
